import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Presentation } from 'lucide-react';
import { getPublicMentors } from '../../api/mentor';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import PageHead from '../../components/PageHead/PageHead';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import Avatar from '../../components/Avatar/Avatar';
import { useI18n } from '../../i18n/I18nContext';
import { formatDate, formatPhone } from '../../utils/datetime';
import './MentorDirectory.css';

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The teachers of the platform, as a read-only list for staff: who they are and how to reach them.
 * (GET /users?role=TEACHER: the accounts, so name, e-mail, phone and status; the optional mentor
 * profile fields — subject, bio, photo — are shown when a row carries them.)
 */
function MentorDirectory() {
  const { t } = useI18n();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // null | 'forbidden' | 'failed'
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  // Only the newest request may answer: typing or paging fast must not let a slow old reply win.
  const latest = useRef(0);

  const fetchMentors = useCallback(async (page = 1) => {
    const id = ++latest.current;
    setLoading(true);
    try {
      // GET /users filters by search and role only: it rejects any other query
      // field (a subject filter would answer 400), so the directory has none.
      const result = await getPublicMentors({ page, limit: PAGE_SIZE, search: search || undefined });
      if (id !== latest.current) return;
      setMentors(result.data || []);
      setPagination({
        page,
        total: result.meta?.total || 0,
        totalPages: result.meta?.totalPages || 0,
      });
      setError(null);
    } catch (err) {
      if (id !== latest.current) return;
      setError(err?.code === 403 ? 'forbidden' : 'failed');
    } finally {
      if (id === latest.current) setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchMentors();
  }, [fetchMentors]);

  // The box answers at once; the list asks the API once the user pauses.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) fetchMentors(pagination.page + 1);
  };

  const handlePreviousPage = () => {
    if (pagination.page > 1) fetchMentors(pagination.page - 1);
  };

  let content;
  if (error) {
    content = (
      <PageEmptyState
        variant={error === 'forbidden' ? 'forbidden' : 'error'}
        size="sm"
        onRetry={error === 'forbidden' ? undefined : () => fetchMentors(pagination.page || 1)}
      />
    );
  } else if (loading && !mentors.length) {
    content = (
      <div className="card-grid mdir-grid" role="status" aria-busy="true" aria-live="polite">
        <span className="visually-hidden">{t('teamCalendar.mentors.loading')}</span>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="mdir-card section-card mdir-skel" aria-hidden="true">
            <span className="ui-skel mdir-skel-avatar" />
            <div className="mdir-info">
              <span className="ui-skel mdir-skel-line" style={{ width: '55%' }} />
              <span className="ui-skel mdir-skel-line is-sub" style={{ width: '85%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (mentors.length === 0) {
    content = query ? (
      <PageEmptyState
        variant="search"
        title={t('teamCalendar.mentors.emptyTitle')}
        description={t('teamCalendar.mentors.emptyDesc')}
        actionLabel={t('teamCalendar.clearFilters')}
        onAction={() => setQuery('')}
      />
    ) : (
      <PageEmptyState
        variant="students"
        title={t('teamCalendar.mentors.noneTitle')}
        description={t('teamCalendar.mentors.noneDesc')}
      />
    );
  } else {
    content = (
      <>
        <div className={`card-grid mdir-grid${loading ? ' mdir-busy' : ''}`} aria-busy={loading || undefined}>
          {mentors.map((mentor) => {
            const photo = mentor.avatar || mentor.profileImage;
            const phone = formatPhone(mentor.phone);
            const status = mentor.isBanned ? 'banned' : mentor.isActive === false ? 'inactive' : null;
            return (
              <article key={mentor.id} className="mdir-card section-card">
                <Avatar name={mentor.name} id={mentor.id} src={photo} size="lg" decorative className="mdir-avatar" />
                <div className="mdir-info">
                  <h2 className="mdir-name">{mentor.name || '—'}</h2>
                  {mentor.email && <p className="mdir-line" title={mentor.email}>{mentor.email}</p>}
                  {phone && <p className="mdir-line">{phone}</p>}
                  {mentor.createdAt && (
                    <p className="mdir-date">{t('teamCalendar.people.joinedOn', { date: formatDate(mentor.createdAt) })}</p>
                  )}
                </div>
                {(mentor.subjectArea || status) && (
                  <div className="mdir-foot">
                    {mentor.subjectArea && <span className="ds-badge ds-badge-brand">{mentor.subjectArea}</span>}
                    {status && (
                      <span className={`ds-badge ds-badge--dot ${status === 'banned' ? 'ds-badge-danger' : 'ds-badge-neutral'}`}>
                        {status === 'banned' ? t('common.banned') : t('team.statusInactive')}
                      </span>
                    )}
                  </div>
                )}
                {mentor.bio && <p className="mdir-bio">{mentor.bio}</p>}
              </article>
            );
          })}
        </div>

        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={PAGE_SIZE}
          onPrev={handlePreviousPage}
          onNext={handleNextPage}
          hideOnSinglePage
        />
      </>
    );
  }

  return (
    <div className="mentor-directory">
      <PageHead icon={<Presentation />} title={t('teamCalendar.mentors.title')} description={t('teamCalendar.mentors.subtitle')} />

      <div className="mdir-filters">
        <SearchInput
          className="mdir-search"
          placeholder={t('teamCalendar.mentors.searchPlaceholder')}
          aria-label={t('teamCalendar.mentors.searchPlaceholder')}
          value={query}
          onChange={setQuery}
        />
      </div>

      {content}
    </div>
  );
}

export default MentorDirectory;
